import React, { useEffect, useMemo, useState } from "react"

import { Avatar, Flex, Spin, Tooltip, Typography } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import Table from "../../Components/Table"
import TitleSection from "../../Components/TitleSection/TitleSection"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import Images from "../../Images"
import { isAllBusinessCompleted } from "../../Utils/onboarding/calculateProgress"
import {
  createAccountStepPath,
  ensureHttps,
  paymentProcessorMap,
  statusTextMap
} from "../../Utils/utils"
import UserActions from "../User/reducer"
import EditBusinessAccountModal from "./BusinessAccounts/EditBussinessAccountModal"
import { columns } from "./BusinessAccounts/Table/columns"

import "./BusinessAccounts.less"

const { Title, Text, Link } = Typography

const statusColorMap = {
  pending: "#661AC6",
  active: "#35BD29",
  needs_help: "#D71313",
  incomplete: "#DF7009",
  unenrolled: "#D71313",
  failed: "#D71313",
  inactive: "#D71313"
}

export const BusinessAccounts = ({
  theme,
  isMobile,
  fetching,
  userInfo,
  businessAccounts,
  businessAccount,
  businessAccountId,
  updateBusinessAccount,
  auth,
  ba_role,

  saveFetching,
  saveResult,

  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const externalDemoMode = useExternalDemoMode()
  const incognito = useIncognito()
  const [query] = useSearchParams()
  const user_id = query.get("user_id")
  const userInfoForAdmin = useSelector((state) => state.user.userInfoForAdmin)

  const isAdminViewingUser = auth?.isAdmin && !!user_id
  const adminFullName = isAdminViewingUser
    ? [userInfoForAdmin?.first_name, userInfoForAdmin?.last_name].filter(Boolean).join(" ")
    : null

  const [redirect, setRedirect] = useState(null)
  const [selectedBusinessAccId, setSelectedBusinessAccId] = useState(null)
  const selectedBusinessAcc = useMemo(() => {
    return businessAccounts?.find((ba) => ba?.id === selectedBusinessAccId)
  }, [businessAccounts, selectedBusinessAccId])

  const isAdmin = auth?.isAdmin

  const handleAddNewAccount = () => {
    props.userSaveResultReset()
    props.changeNextCreateAccountStep(1)
    props.setRedirectAfterCreateAccount(location?.pathname)
    props.getBusinessAccounts()
    navigate(createAccountStepPath[1])
  }

  const isDemoModeBlocked = !userInfo.onboarding_finished && !userInfo.new_onboarding_finished

  useEffect(() => {
    if (!businessAccountId && !isAdminViewingUser) return

    props.getBusinessAccounts(isAdminViewingUser ? { user_id } : undefined)
  }, [businessAccountId, isAdminViewingUser, user_id])

  useEffect(() => {
    if (saveResult && redirect) {
      navigate(redirect)
      props.userSaveResultReset()
    }
  }, [saveResult, redirect])

  const onSelectRow = (newBusinessAccountId, type) => {
    if (type === "business_account") {
      props.selectBusinessAccountId(newBusinessAccountId)
      const localTestingUri = "http://localhost:3000"
      const isLocalHost = window.location.origin === localTestingUri
      window.open(
        `${isLocalHost ? localTestingUri : process.env.REACT_APP_BASE_URI}/business_settings/enrollment`
      )
      return
    }
    if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${newBusinessAccountId}`)
      return
    }
    if (isAdmin) {
      props.changeBusinessAccount(newBusinessAccountId)
    }
    if (type === "business_settings") {
      isAdmin
        ? setRedirect("/business_settings/enrollments")
        : setSelectedBusinessAccId(newBusinessAccountId)
    } else if (type === "add_payment_processor") {
      setRedirect("/integrations")
    }
  }

  if (isMobile) {
    return (
      <div className="business-accounts-mobile">
        <div className="business-accounts-mobile__header">
          <Title level={4} className="business-accounts-mobile__title">
            {t("Business accounts")}
          </Title>
          <Text type="secondary" className="business-accounts-mobile__subtitle">
            {t("An overview of your business accounts.")}
          </Text>
        </div>

        <ButtonComponent
          type="primary"
          style={{ width: "100%", height: 40, borderRadius: 12, marginBottom: 16 }}
          onClick={externalDemoMode ? null : handleAddNewAccount}
          disabled={
            userInfo?.is_demo || externalDemoMode || !isAllBusinessCompleted(businessAccounts ?? [])
          }
        >
          {t("Create business account")}
        </ButtonComponent>

        <Spin spinning={fetching || saveFetching}>
          <Flex vertical gap={12} className="business-accounts-mobile__cards">
            {(businessAccounts ?? []).map((record) => {
              const statusColor = statusColorMap[record.status] || "#7A7A83"
              const statusLabel = statusTextMap[record.status] || record.status

              return (
                <div key={record.id} className="business-accounts-mobile__card">
                  <div className="business-accounts-mobile__card-name">
                    <Avatar
                      size={24}
                      src={record.photo}
                      style={{
                        backgroundColor: record.photo ? "transparent" : "#225AEA",
                        borderRadius: 6,
                        border: "none",
                        fontSize: 12,
                        flexShrink: 0
                      }}
                    >
                      {!record.photo ? record.name?.[0] || "" : null}
                    </Avatar>
                    <Text strong style={{ fontSize: 16, letterSpacing: "-0.3px" }}>
                      {record.name}
                    </Text>
                  </div>

                  <div className="business-accounts-mobile__card-row">
                    <div className="business-accounts-mobile__card-field">
                      <span className="business-accounts-mobile__card-label">
                        {t("Business URL")}
                      </span>
                      <Link
                        href={ensureHttps(record.url)}
                        target="_blank"
                        className="business-accounts-mobile__card-link"
                      >
                        {record.url}
                      </Link>
                    </div>
                    <div className="business-accounts-mobile__card-field">
                      <span className="business-accounts-mobile__card-label">
                        {t("Payment processor")}
                      </span>
                      <Avatar.Group size="small">
                        {record.connected_payment_processors?.length ? (
                          record.connected_payment_processors.map((el) => (
                            <Tooltip title={paymentProcessorMap[el] || el} key={el}>
                              <Avatar
                                src={Images[`integration_${el}`]}
                                style={{ borderRadius: 4, width: 20, height: 20 }}
                              />
                            </Tooltip>
                          ))
                        ) : (
                          <Tooltip title={t("Add payment processor")}>
                            <Avatar
                              icon={
                                <Icon
                                  type={theme === "dark" ? "add_account_dark" : "add_account"}
                                  style={{ fontSize: 18 }}
                                />
                              }
                              style={{ borderRadius: 4, width: 20, height: 20, cursor: "pointer" }}
                              onClick={() => onSelectRow(record.id, "add_payment_processor")}
                            />
                          </Tooltip>
                        )}
                      </Avatar.Group>
                    </div>
                  </div>

                  <div className="business-accounts-mobile__card-row">
                    <div className="business-accounts-mobile__card-field">
                      <span className="business-accounts-mobile__card-label">
                        {t("Credit card")}
                      </span>
                      <div className="business-accounts-mobile__card-cc">
                        {record.card?.brand && (
                          <img
                            src={Images[`${record.card.brand}${theme === "dark" ? "_dark" : ""}`]}
                            alt={record.card.brand}
                            style={{ width: 20, height: 20 }}
                          />
                        )}
                        {record.card?.last4 && (
                          <Text style={{ fontSize: 14, letterSpacing: "-0.3px" }}>
                            {`•••• ${record.card.last4}`}
                          </Text>
                        )}
                        {!record.card?.last4 && <Text type="secondary">—</Text>}
                      </div>
                    </div>
                    <div className="business-accounts-mobile__card-field">
                      <span className="business-accounts-mobile__card-label">{t("Status")}</span>
                      <Text
                        style={{
                          fontSize: 14,
                          letterSpacing: "-0.3px",
                          color: statusColor,
                          fontWeight: 500
                        }}
                      >
                        {statusLabel}
                      </Text>
                    </div>
                  </div>

                  <ButtonComponent
                    style={{ width: "100%", height: 36 }}
                    icon={<Icon type="edit_big" />}
                    onClick={() => onSelectRow(record.id, "business_settings")}
                  >
                    {t("Edit")}
                  </ButtonComponent>
                </div>
              )
            })}
          </Flex>
        </Spin>

        <EditBusinessAccountModal
          open={selectedBusinessAccId !== null && selectedBusinessAcc !== null}
          closeModal={() => setSelectedBusinessAccId(null)}
          saveFetching={saveFetching}
          businessAccountId={selectedBusinessAccId}
          businessAccount={selectedBusinessAcc}
          updateBusinessAccount={updateBusinessAccount}
          ba_role={ba_role}
        />
      </div>
    )
  }

  return (
    <div>
      <TitleSection
        icon="bullet_list"
        stacked={isAdminViewingUser}
        title={`Business accounts${adminFullName ? ` for ${adminFullName}` : ""}`}
        description={
          isAdminViewingUser ? userInfoForAdmin?.email : "An overview of your business accounts."
        }
        style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
      >
        {!isAdmin && (
          <ButtonComponent
            type="primary"
            onClick={externalDemoMode ? null : handleAddNewAccount}
            disabled={
              userInfo?.is_demo ||
              externalDemoMode ||
              !isAllBusinessCompleted(businessAccounts ?? [])
            }
          >
            {t("Create business account")}
          </ButtonComponent>
        )}
      </TitleSection>

      <Table
        columns={columns(onSelectRow, t, theme, isDemoModeBlocked, isAdminViewingUser, incognito)}
        loading={fetching || saveFetching}
        dataSource={businessAccounts}
        withOutPagination={true}
        scroll={{ x: 1000 }}
      />

      <EditBusinessAccountModal
        open={selectedBusinessAccId !== null && selectedBusinessAcc !== null}
        closeModal={() => setSelectedBusinessAccId(null)}
        saveFetching={saveFetching}
        businessAccountId={selectedBusinessAccId}
        businessAccount={selectedBusinessAcc}
        updateBusinessAccount={updateBusinessAccount}
        ba_role={ba_role}
      />
    </div>
  )
}

BusinessAccounts.propTypes = {
  fetching: PropTypes.bool,
  businessAccounts: PropTypes.array,
  isMobile: PropTypes.bool
}

const mapStateToProps = (state) => ({
  theme: state.nav.theme,
  isMobile: state.nav.isMobile,
  userInfo: state.auth.userInfo,
  auth: state.auth,
  businessAccountId: state.auth.userInfo.current_business_account_id,

  fetching: state.user.fetching,
  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount,
  ba_role: state.auth.userInfo?.ba_role?.id,

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult
})

const mapDispatchToProps = (dispatch) => ({
  getBusinessAccounts: (params) => dispatch(UserActions.getBusinessAccountsRequest(params)),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(UserActions.userSaveRequest({ current_business_account_id: newBusinessAccountId })),
  selectBusinessAccountId: (businessAccountId) =>
    dispatch(UserActions.userSetProp("admin_selected_business_account_id", businessAccountId)),
  userSaveResultReset: () => dispatch(UserActions.userSaveResultReset()),
  updateBusinessAccount: (data) => dispatch(UserActions.updateBusinessAccountRequest(data)),
  changeNextCreateAccountStep: (step) => dispatch(UserActions.changeNextCreateAccountStep(step)),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(UserActions.userSetProp("redirectAfterCreateAccount", location))
})

export default connect(mapStateToProps, mapDispatchToProps)(BusinessAccounts)
